import { useMemo } from 'react';
import { getDateLocale } from '../../../lib/base-i18n';
import { fromDayKey } from '../Calendar/calendar-core';
import './CalendarGrid.css';

/**
 * CalendarGrid — **«보는» 달력** (디자인 정본 C6 「달력 격자」).
 *
 * ★★★ **C1 `Calendar` 와는 다른 물건이다.** 그쪽은 «고르는» 달력(DatePicker 가 그 위에 선다)이고
 *    이것은 **기간 안에 무슨 일이 있었는지를 칸에 펴는** 달력이다. 클래스 이름도 갈라 둔다 —
 *    `wc-cal*` 는 이미 C1 의 것이라, 같은 이름을 쓰면 **둘 중 하나가 소리 없이 깨진다**
 *    (실제로 이름을 짓다 부딪혀 갈랐다).
 *
 * ★★★ **매일 · 모두에게 있는 값을 칸에 넣지 않는다.** 근태 현황이라면 출근 · 미출근이 그것이다 —
 *    칸마다 마흔 줄이 서고 그러면 **정작 비는 날이 묻힌다**. 달력은 「누가 언제 없나」에만 답하고,
 *    하루의 값은 **아래 표**가 맡는다.
 *
 * ★★ **월/주는 다른 화면이 아니다.** 같은 7열 격자에서 줄 수와 칸 깊이만 바뀐다 —
 *    두 벌로 만들면 칸 안의 규칙(항목 수 · 말줄임 · 고른 칸)이 갈라진다.
 *
 * ★ **「+N」은 단추가 아니다.** 칸 자체가 단추라 그 안에 또 단추를 넣으면 파서가 바깥을 먼저 닫는다.
 *   더 볼 것은 **칸을 눌러** 아래 표에서 본다 — 그래서 「+N」은 «세어 보인 말»이면 충분하다.
 */
export type CalendarItemTone = 'default' | 'info' | 'success' | 'warning' | 'danger';

export interface CalendarGridItem {
  id: string;
  /** 한 줄로 적는다. 넘치면 말줄임 — **색만으로 갈래를 만들지 않는다**(정본 P5). */
  label: string;
  tone?: CalendarItemTone;
}

export interface CalendarGridDay {
  /** `YYYY-MM-DD`. */
  date: string;
  /** 이 달의 날인가. 앞뒤로 삐져나온 칸은 옅게 그린다. 주간에서는 늘 참이다. */
  inMonth?: boolean;
  /** 쉬는 날이면 그 이름 — 「추석」·「추석 대체」. */
  holiday?: { name: string; isSubstitute?: boolean } | null;
  items?: CalendarGridItem[];
}

export interface CalendarGridProps {
  days: readonly CalendarGridDay[];
  /** 주간이면 한 줄이고 칸이 깊어진다(항목이 더 선다). */
  week?: boolean;
  /** 고른 칸 — `YYYY-MM-DD`. 아래 표가 이 날을 그린다. */
  selected?: string | null;
  /** 오늘 — 알약으로 표시. 안 주면 아무 칸도 오늘이 아니다(지어내지 않는다). */
  today?: string | null;
  onSelect?: (date: string) => void;
  /** 칸에 세울 항목 수. 넘치면 「+N」. 기본은 월간 3 · 주간 8. */
  maxItems?: number;
  ariaLabel?: string;
  className?: string;
}

/** 요일 이름은 만들지 않는다 — `Intl` 이 로케일에 맞춰 준다(C1 과 같은 규칙). */
function useWeekdayNames(): string[] {
  const locale = getDateLocale();
  return useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 1970-01-04 = 일요일. 격자가 일요일부터라 그날부터 이레를 뽑는다.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(1970, 0, 4 + i))));
  }, [locale]);
}

export function CalendarGrid({
  days, week = false, selected, today, onSelect, maxItems, ariaLabel, className,
}: CalendarGridProps) {
  const weekdays = useWeekdayNames();
  const cap = maxItems ?? (week ? 8 : 3);

  return (
    <div
      className={['wc-cgrid', week ? 'wc-cgrid--week' : '', className ?? ''].filter(Boolean).join(' ')}
      role="grid"
      aria-label={ariaLabel}
    >
      <div className="wc-cgrid__dow" role="row">
        {weekdays.map((w, i) => (
          <span
            key={w}
            role="columnheader"
            /* 토 · 일을 다르게 — 한국 달력의 관습이고, 둘을 같은 붉은색으로 두면 휴일과 헷갈린다. */
            className={['wc-cgrid__wd', i === 0 ? 'is-sun' : '', i === 6 ? 'is-sat' : ''].filter(Boolean).join(' ')}
          >{w}</span>
        ))}
      </div>
      <div className="wc-cgrid__grid" role="rowgroup">
        {days.map((d) => {
          const items = d.items ?? [];
          const shown = items.slice(0, cap);
          const rest = items.length - shown.length;
          const dt = fromDayKey(d.date);
          const dow = dt ? dt.getDay() : -1;
          const isToday = !!today && d.date === today;
          const cls = [
            'wc-cgrid__day',
            d.inMonth === false ? 'is-out' : '',
            d.holiday ? 'is-holiday' : '',
            !d.holiday && dow === 0 ? 'is-sun' : '',
            selected === d.date ? 'is-sel' : '',
          ].filter(Boolean).join(' ');

          const body = (
            <>
              <span className="wc-cgrid__d">
                <span className={isToday ? 'wc-cgrid__today' : undefined}>{dt ? dt.getDate() : d.date}</span>
                {d.holiday && (
                  <span className="wc-cgrid__tag" title={d.holiday.name}>
                    {d.holiday.isSubstitute ? '대체' : d.holiday.name}
                  </span>
                )}
              </span>
              {shown.map((it) => (
                <span key={it.id} className={`wc-cgrid__it is-${it.tone ?? 'default'}`} title={it.label}>
                  <i aria-hidden />{it.label}
                </span>
              ))}
              {rest > 0 && (
                /* 단추가 아니다 — 칸을 누르면 아래 표에 전부 선다. */
                <span className="wc-cgrid__more">+{rest}</span>
              )}
            </>
          );

          if (!onSelect) return <div key={d.date} className={cls} role="gridcell">{body}</div>;
          return (
            <button
              key={d.date}
              type="button"
              role="gridcell"
              className={cls}
              aria-pressed={selected === d.date}
              aria-label={`${d.date}${items.length > 0 ? ` · ${items.length}건` : ''}`}
              onClick={() => onSelect(d.date)}
            >{body}</button>
          );
        })}
      </div>
    </div>
  );
}
